import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import styles from './AddCenterModal.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const AddCenterModal = ({ isOpen, onClose, onSubmit, centerData, isEditing = false }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    Adı: '',
    İl: '',
    İlçe: '',
    Yetkili: '',
    Adres: '',
    Bilgi: '',
    SMO: '',
    SMOBackup: '',
    Durum: 'Aktif'
  });
  
  const [employees, setEmployees] = useState([]);
  const [externalPersonnels, setExternalPersonnels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If editing, populate form with existing data
    if (isEditing && centerData) {
      const yetkiliMatch = externalPersonnels.find(p => p.id === centerData.Yetkili);
      const smoMatch = employees.find(e => e.id === centerData.SMO);
      const smoBackupMatch = employees.find(e => e.id === centerData.SMOBackup);

      setFormData({
        Adı: centerData.Adı || '',
        İl: centerData.İl || '',
        İlçe: centerData.İlçe || '',
        Yetkili: yetkiliMatch ? yetkiliMatch.fullName : (centerData.Yetkili || ''),
        Adres: centerData.Adres || '',
        Bilgi: centerData.Bilgi || '',
        SMO: smoMatch ? smoMatch.fullName : (centerData.SMO || ''),
        SMOBackup: smoBackupMatch ? smoBackupMatch.fullName : (centerData.SMOBackup || ''),
        Durum: centerData.Durum || 'Aktif'
      });
    }
  }, [isEditing, centerData, employees, externalPersonnels]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeesCollection = collection(db, 'Employees');
        const employeeSnapshot = await getDocs(employeesCollection);
        const employeeList = employeeSnapshot.docs
          .filter(doc => doc.data().Active === 1) // Sadece aktif çalışanlar (Active = 1)
          .map(doc => ({
            id: doc.id,
            fullName: `${doc.data().eName} ${doc.data().eSurname}`,
            data: doc.data()
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
        setEmployees(employeeList);
        console.log('✅ Aktif çalışanlar yüklendi:', employeeList.length);
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error('Çalışanlar yüklenirken bir hata oluştu');
      }
    };

    const fetchExternalPersonnels = async () => {
      try {
        const externalPersonnelsCollection = collection(db, 'ExternalPersonnels');
        const externalPersonnelSnapshot = await getDocs(externalPersonnelsCollection);
        
        // Debug: Tüm personelleri logla
        console.log('📋 Tüm harici personeller:', externalPersonnelSnapshot.docs.map(doc => ({
          id: doc.id,
          name: `${doc.data().Adı} ${doc.data().Soyadı}`,
          durum: doc.data().Durum
        })));
        
        const externalPersonnelList = externalPersonnelSnapshot.docs
          .filter(doc => {
            const durum = doc.data().Durum;
            const isActive = durum && (
              durum.toLowerCase() === 'aktif' || 
              durum === 'Aktif' ||
              durum === 'aktif'
            );
            
            // Debug: Filtrelenen personelleri logla
            if (!isActive) {
              console.log('❌ Filtrelendi:', `${doc.data().Adı} ${doc.data().Soyadı}`, 'Durum:', durum);
            }
            
            return isActive;
          })
          .map(doc => ({
            id: doc.id,
            fullName: `${doc.data().Adı} ${doc.data().Soyadı}`,
            data: doc.data()
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
        
        setExternalPersonnels(externalPersonnelList);
        console.log('✅ Aktif harici personeller yüklendi:', externalPersonnelList.length);
        console.log('📝 Aktif personel listesi:', externalPersonnelList.map(p => p.fullName));
      } catch (error) {
        console.error('Error fetching external personnels:', error);
        toast.error('Yetkililer yüklenirken bir hata oluştu');
      }
    };

    fetchEmployees();
    fetchExternalPersonnels();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Find selected employee documents
      const selectedYetkili = externalPersonnels.find(emp => emp.fullName === formData.Yetkili);
      const selectedSMO = employees.find(emp => emp.fullName === formData.SMO);
      const selectedSMOBackup = employees.find(emp => emp.fullName === formData.SMOBackup);

      if (isEditing) {
        // Update existing center
        const centerRef = doc(db, 'Centers', centerData.id);
        const updatedCenter = {
          ...formData,
          Yetkili: selectedYetkili?.id || formData.Yetkili || '',
          SMO: selectedSMO?.id || formData.SMO || '',
          SMOBackup: selectedSMOBackup?.id || formData.SMOBackup || '',
          updatedAt: new Date(),
        };

        await updateDoc(centerRef, updatedCenter);
        toast.success('Merkez başarıyla güncellendi!');
      } else {
        // Add new center
        const centersRef = collection(db, 'Centers');
        const newCenter = {
          ...formData,
          Yetkili: selectedYetkili?.id || '',
          SMO: selectedSMO?.id || '',
          SMOBackup: selectedSMOBackup?.id || '',
          createdAt: new Date(),
        };

        await addDoc(centersRef, newCenter);
        toast.success('Merkez başarıyla eklendi!');
      }
      
      if (onSubmit) {
        onSubmit(formData);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error with center operation:', error);
      toast.error(isEditing 
        ? 'Merkez güncellenirken bir hata oluştu!' 
        : 'Merkez eklenirken bir hata oluştu!'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditing && centerData) {
      const yetkiliMatch = externalPersonnels.find(p => p.id === centerData.Yetkili);
      const smoMatch = employees.find(e => e.id === centerData.SMO);

      const smoBackupMatch = employees.find(e => e.id === centerData.SMOBackup);
      setFormData({
        Adı: centerData.Adı || '',
        İl: centerData.İl || '',
        İlçe: centerData.İlçe || '',
        Yetkili: yetkiliMatch ? yetkiliMatch.fullName : (centerData.Yetkili || ''),
        Adres: centerData.Adres || '',
        Bilgi: centerData.Bilgi || '',
        SMO: smoMatch ? smoMatch.fullName : (centerData.SMO || ''),
        SMOBackup: smoBackupMatch ? smoBackupMatch.fullName : (centerData.SMOBackup || ''),
        Durum: centerData.Durum || 'Aktif'
      });
    } else {
      setFormData({
        Adı: '',
        İl: '',
        İlçe: '',
        Yetkili: '',
        Adres: '',
        Bilgi: '',
        SMO: '',
        SMOBackup: '',
        Durum: 'Aktif'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.modalBackdrop} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.modalContent} ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEditing ? 'Merkez Düzenle' : 'Yeni Merkez Ekle'}
          </h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Adı</label>
              <input
                type="text"
                name="Adı"
                value={formData.Adı}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>İl</label>
                <select
                  name="İl"
                  value={formData.İl}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Seçiniz</option>
                  <option value="Adana">Adana</option>
                  <option value="Adıyaman">Adıyaman</option>
                  <option value="Afyonkarahisar">Afyonkarahisar</option>
                  <option value="Ağrı">Ağrı</option>
                  <option value="Amasya">Amasya</option>
                  <option value="Ankara">Ankara</option>
                  <option value="Antalya">Antalya</option>
                  <option value="Artvin">Artvin</option>
                  <option value="Aydın">Aydın</option>
                  <option value="Balıkesir">Balıkesir</option>
                  <option value="Bilecik">Bilecik</option>
                  <option value="Bingöl">Bingöl</option>
                  <option value="Bitlis">Bitlis</option>
                  <option value="Bolu">Bolu</option>
                  <option value="Burdur">Burdur</option>
                  <option value="Bursa">Bursa</option>
                  <option value="Çanakkale">Çanakkale</option>
                  <option value="Çankırı">Çankırı</option>
                  <option value="Çorum">Çorum</option>
                  <option value="Denizli">Denizli</option>
                  <option value="Diyarbakır">Diyarbakır</option>
                  <option value="Edirne">Edirne</option>
                  <option value="Elazığ">Elazığ</option>
                  <option value="Erzincan">Erzincan</option>
                  <option value="Erzurum">Erzurum</option>
                  <option value="Eskişehir">Eskişehir</option>
                  <option value="Gaziantep">Gaziantep</option>
                  <option value="Giresun">Giresun</option>
                  <option value="Gümüşhane">Gümüşhane</option>
                  <option value="Hakkari">Hakkari</option>
                  <option value="Hatay">Hatay</option>
                  <option value="Isparta">Isparta</option>
                  <option value="Mersin">Mersin</option>
                  <option value="İstanbul">İstanbul</option>
                  <option value="İzmir">İzmir</option>
                  <option value="Kars">Kars</option>
                  <option value="Kastamonu">Kastamonu</option>
                  <option value="Kayseri">Kayseri</option>
                  <option value="Kırklareli">Kırklareli</option>
                  <option value="Kırşehir">Kırşehir</option>
                  <option value="Kocaeli">Kocaeli</option>
                  <option value="Konya">Konya</option>
                  <option value="Kütahya">Kütahya</option>
                  <option value="Malatya">Malatya</option>
                  <option value="Manisa">Manisa</option>
                  <option value="Kahramanmaraş">Kahramanmaraş</option>
                  <option value="Mardin">Mardin</option>
                  <option value="Muğla">Muğla</option>
                  <option value="Muş">Muş</option>
                  <option value="Nevşehir">Nevşehir</option>
                  <option value="Niğde">Niğde</option>
                  <option value="Ordu">Ordu</option>
                  <option value="Rize">Rize</option>
                  <option value="Sakarya">Sakarya</option>
                  <option value="Samsun">Samsun</option>
                  <option value="Siirt">Siirt</option>
                  <option value="Sinop">Sinop</option>
                  <option value="Sivas">Sivas</option>
                  <option value="Tekirdağ">Tekirdağ</option>
                  <option value="Tokat">Tokat</option>
                  <option value="Trabzon">Trabzon</option>
                  <option value="Tunceli">Tunceli</option>
                  <option value="Şanlıurfa">Şanlıurfa</option>
                  <option value="Uşak">Uşak</option>
                  <option value="Van">Van</option>
                  <option value="Yozgat">Yozgat</option>
                  <option value="Zonguldak">Zonguldak</option>
                  <option value="Aksaray">Aksaray</option>
                  <option value="Bayburt">Bayburt</option>
                  <option value="Karaman">Karaman</option>
                  <option value="Kırıkkale">Kırıkkale</option>
                  <option value="Batman">Batman</option>
                  <option value="Şırnak">Şırnak</option>
                  <option value="Bartın">Bartın</option>
                  <option value="Ardahan">Ardahan</option>
                  <option value="Iğdır">Iğdır</option>
                  <option value="Yalova">Yalova</option>
                  <option value="Karabük">Karabük</option>
                  <option value="Kilis">Kilis</option>
                  <option value="Osmaniye">Osmaniye</option>
                  <option value="Düzce">Düzce</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>İlçe</label>
                <input
                  type="text"
                  name="İlçe"
                  value={formData.İlçe}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Yetkili</label>
                <select
                  name="Yetkili"
                  value={formData.Yetkili}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="">Seçiniz</option>
                  {externalPersonnels.map(emp => (
                    <option key={emp.id} value={emp.fullName}>
                      {emp.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>SMO</label>
                <select
                  name="SMO"
                  value={formData.SMO}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="">Seçiniz</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.fullName}>
                      {emp.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>SMO Yedek (Backup)</label>
              <select
                name="SMOBackup"
                value={formData.SMOBackup}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="">Seçiniz</option>
                {employees
                  .filter(emp => emp.fullName !== formData.SMO)
                  .map(emp => (
                    <option key={emp.id} value={emp.fullName}>
                      {emp.fullName}
                    </option>
                  ))}
              </select>
            </div>

            {isEditing && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Durum</label>
                <select
                  name="Durum"
                  value={formData.Durum}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="Aktif">Aktif</option>
                  <option value="İnaktif">İnaktif</option>
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.label}>Adres</label>
              <textarea
                name="Adres"
                value={formData.Adres}
                onChange={handleChange}
                className={styles.textarea}
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Bilgi</label>
              <textarea
                name="Bilgi"
                value={formData.Bilgi}
                onChange={handleChange}
                className={styles.textarea}
                rows={3}
              />
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleReset}
                className={`${styles.button} ${styles.resetButton}`}
                disabled={loading}
              >
                Sıfırla
              </button>
              <button
                type="submit"
                className={`${styles.button} ${styles.submitButton}`}
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : isEditing ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCenterModal;